/**
 * Socket.IO client for real-time communication with backend.
 */

import { io, Socket } from 'socket.io-client'
import i18next from 'i18next'
import { config } from '../config'
import { useGameStore } from '@/stores/gameStore'
import { usePlayerStore } from '@/stores/playerStore'
import { useUIStore } from '@/stores/uiStore'
import { useAuthStore } from '@/stores/authStore'
import { GameState, GameStateDelta } from '@/types/game'
import { detectSupportedLocale } from '@/types/auth'
import { registerGlobalListeners, unregisterGlobalListeners } from './socketEventRouter'

// Socket instance - created lazily on connect
let socket: Socket | null = null

// Component subscriptions (onEvent), re-attached to every new socket instance
type SocketEventHandler = (data: unknown) => void
const eventRegistry = new Map<string, Set<SocketEventHandler>>()

// Session invalidation callback (set by useSocket hook)
let onSessionInvalidCallback: ((reason: string) => void) | null = null

// Connection status as an external store, so every useSocket() caller reads one
// shared truth instead of keeping its own copy in component state.
type ConnectionListener = () => void
const connectionListeners = new Set<ConnectionListener>()
let connectedSnapshot = false
let isConnecting = false

function notifyConnectionListeners(): void {
  connectionListeners.forEach((listener) => listener())
}

function publishConnectionState(next: boolean): void {
  if (connectedSnapshot === next) return
  connectedSnapshot = next
  notifyConnectionListeners()
}

function setConnecting(next: boolean): void {
  if (isConnecting === next) return
  isConnecting = next
  notifyConnectionListeners()
}

/** Subscribe to connection status changes. Returns an unsubscribe function. */
export function subscribeSocketStatus(listener: ConnectionListener): () => void {
  connectionListeners.add(listener)
  return () => {
    connectionListeners.delete(listener)
  }
}

/** Current connection status. Boolean snapshot, safe for useSyncExternalStore. */
export function getSocketConnectedSnapshot(): boolean {
  return connectedSnapshot
}

/** True while a connection attempt is in flight. */
export function getSocketConnectingSnapshot(): boolean {
  return isConnecting
}

export interface SocketAuth {
  session_id: string
  player_id: string
}

/**
 * Connect to Socket.IO server with authentication.
 *
 * @param auth - Authentication data (session_id, player_id)
 * @returns Promise that resolves when connected
 */
export async function connectSocket(auth: SocketAuth): Promise<void> {
  if (socket?.connected) {
    console.log('[Socket] Already connected')
    return
  }

  if (isConnecting) {
    console.log('[Socket] Connection in progress')
    return
  }

  setConnecting(true)

  // A previous instance still reconnecting on its own would become a second
  // live connection of the same player; close it before creating a new one.
  if (socket !== null) {
    unregisterGlobalListeners()
    socket.removeAllListeners()
    socket.disconnect()
    socket = null
  }

  return new Promise((resolve, reject) => {
    socket = io(config.wsUrl, {
      auth,
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 15,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
    })
    attachRegisteredHandlers(socket)

    socket.on('connect', () => {
      console.log('[Socket] Connected:', socket?.id)
      setConnecting(false)
      if (socket) registerGlobalListeners(socket)
      publishConnectionState(true)
      resolve()
    })

    socket.on('connect_error', (error) => {
      console.error('[Socket] Connection error:', error.message)
      setConnecting(false)
      publishConnectionState(false)

      // Server explicitly rejected — player not found or session closed.
      // Retrying won't change the outcome, so invalidate immediately.
      if (error.message.includes('Connection rejected by server')) {
        console.warn('[Socket] Session invalid - triggering cleanup')
        if (onSessionInvalidCallback) {
          onSessionInvalidCallback(error.message)
        }
      }

      reject(error)
    })

    socket.on('disconnect', (reason) => {
      console.log('[Socket] Disconnected:', reason)
      publishConnectionState(false)
    })
  })
}

/**
 * Disconnect from Socket.IO server.
 */
export function disconnectSocket(): void {
  if (socket) {
    unregisterGlobalListeners()
    socket.disconnect()
    socket = null
    publishConnectionState(false)
    console.log('[Socket] Disconnected manually')

    // Revert locale to profile locale (logged-in) or navigator locale (guest)
    usePlayerStore.getState().setSessionLocale(null)
    const user = useAuthStore.getState().user
    if (user) {
      i18next.changeLanguage(user.locale)
    } else {
      // Guest — revert to navigator-detected locale, NOT hardcoded 'pl'
      const detected = detectSupportedLocale(navigator.language)
      i18next.changeLanguage(detected)
    }
  }
}

/**
 * Get the current socket instance.
 * Returns null if not connected.
 */
export function getSocket(): Socket | null {
  return socket
}

/**
 * Check if socket is currently connected.
 */
export function isSocketConnected(): boolean {
  return socket?.connected ?? false
}

/**
 * Register callback for session invalidation (connection rejected).
 * Used by useSocket hook to handle cleanup + navigation.
 */
export function setSessionInvalidCallback(callback: ((reason: string) => void) | null): void {
  onSessionInvalidCallback = callback
}

/**
 * Emit an event to the server.
 */
export function emitEvent<T = unknown>(event: string, data?: T): void {
  if (!socket?.connected) {
    console.warn('[Socket] Cannot emit - not connected')
    return
  }
  socket.emit(event, data)
}

/**
 * Subscribe to a server event. Returns unsubscribe function.
 *
 * Subscriptions live in a registry independent of the socket instance: they are
 * attached to the current socket (if any) and to every socket created later by
 * connectSocket(), so a component subscribed once keeps receiving events after
 * a new io() instance replaces the old one.
 */
export function onEvent<T = unknown>(
  event: string,
  handler: (data: T) => void
): () => void {
  const listener = handler as SocketEventHandler
  let handlers = eventRegistry.get(event)
  if (handlers === undefined) {
    handlers = new Set()
    eventRegistry.set(event, handlers)
  }
  handlers.add(listener)
  socket?.on(event, listener)

  return () => {
    eventRegistry.get(event)?.delete(listener)
    socket?.off(event, listener)
  }
}

function attachRegisteredHandlers(target: Socket): void {
  eventRegistry.forEach((handlers, event) => {
    handlers.forEach((listener) => target.on(event, listener))
  })
}

// Reconnect state tracking
const MAX_RECONNECT_ATTEMPTS = 3
const RECONNECT_DELAYS = [1000, 2000, 4000] // Exponential backoff

/**
 * Fetch full state with exponential backoff.
 *
 * CRITICAL: Prevents infinite reconnect loops by:
 * 1. Limiting to MAX_RECONNECT_ATTEMPTS (3)
 * 2. Using exponential backoff (1s, 2s, 4s)
 * 3. Showing error UI after all retries fail
 */
async function fetchFullStateWithRetry(): Promise<boolean> {
  const { sessionId } = useGameStore.getState()
  const { playerId } = usePlayerStore.getState()

  if (!sessionId || !playerId) {
    console.error('[Sync] Cannot fetch state - missing session or player ID')
    return false
  }

  for (let attempt = 0; attempt < MAX_RECONNECT_ATTEMPTS; attempt++) {
    try {
      await useGameStore.getState().fetchState(sessionId, playerId)
      console.log(`[Sync] Full state recovered (attempt ${attempt + 1})`)
      return true
    } catch (error) {
      console.error(`[Sync] Attempt ${attempt + 1}/${MAX_RECONNECT_ATTEMPTS} failed:`, error)

      if (attempt < MAX_RECONNECT_ATTEMPTS - 1) {
        await new Promise((resolve) => setTimeout(resolve, RECONNECT_DELAYS[attempt]))
      }
    }
  }

  // All retries failed - show persistent error
  useUIStore.getState().addToast({
    type: 'error',
    message: i18next.t('connection.lostRefreshToast'),
    duration: 0, // Persistent until dismissed
  })

  return false
}

/**
 * Set up delta sync listener.
 * Listens for game:state:update events and applies deltas to gameStore.
 *
 * @returns Cleanup function to remove listeners
 */
export function setupStateSyncListener(): () => void {
  const sock = getSocket()
  if (!sock) return () => {}

  const handleStateUpdate = async (data: { delta: GameStateDelta; timestamp: number }) => {
    console.log('[Socket] State delta received:', data.delta)

    const currentVersion = useGameStore.getState().version

    // Version mismatch detection - CRITICAL
    if (data.delta.version !== undefined && data.delta.version !== currentVersion + 1) {
      console.warn(
        `[Sync] Version mismatch: expected ${currentVersion + 1}, got ${data.delta.version}`
      )

      // Automatically fetch full state - don't apply stale delta
      await fetchFullStateWithRetry()
      return
    }

    // Apply delta to store (gameStore.applyDelta handles granular players)
    useGameStore.getState().applyDelta(data.delta)

    // Locale sync: if delta contains locale, apply it (priority 1)
    if (data.delta.locale !== undefined) {
      i18next.changeLanguage(data.delta.locale)
      usePlayerStore.getState().setSessionLocale(data.delta.locale)
    }
  }

  const handleFullState = (data: { state: GameState; timestamp: number }) => {
    console.log('[Socket] Full state received')

    // Replace entire state
    useGameStore.setState(data.state as Partial<GameState>)
  }

  sock.on('game:state:update', handleStateUpdate)
  sock.on('game:state:full', handleFullState)

  // Return cleanup function
  return () => {
    sock.off('game:state:update', handleStateUpdate)
    sock.off('game:state:full', handleFullState)
  }
}

/**
 * Set up BINGO event handlers.
 * Listens for game:started, game:card_generated, game:number_called, and game:winner_declared events.
 *
 * @returns Cleanup function to remove listeners
 */
export function setupBingoEventHandlers(): () => void {
  const sock = getSocket()
  if (!sock) return () => {}

  // Handle game started event
  const handleGameStarted = (data: { game_mode: string; calling_speed_seconds: number }) => {
    console.log('[Bingo] Game started:', data)
    useGameStore.getState().applyDelta({ phase: 'playing' })
  }

  // Handle card generation (private per player)
  // Event uses 2-segment domain:action pattern per Epic 2
  const handleCardGenerated = (data: { player_id: string; card: number[][] }) => {
    const { playerId } = usePlayerStore.getState()
    if (data.player_id === playerId) {
      console.log('[Bingo] Card generated for player')
      useGameStore.getState().setBingoCard(data.card)
    }
  }

  // Handle number called
  // Event uses 2-segment domain:action pattern per Epic 2
  const handleNumberCalled = (data: { number: number; total_called: number }) => {
    console.log('[Bingo] Number called:', data.number)
    useGameStore.getState().addCalledNumber(data.number)
  }

  // Handle winner declared
  // Event uses 2-segment domain:action pattern per Epic 2
  const handleWinnerDeclared = (data: { player_id: string }) => {
    console.log('[Bingo] Winner declared:', data.player_id)
    useGameStore.getState().setBingoWinner(data.player_id)
  }

  sock.on('game:started', handleGameStarted)
  sock.on('game:card_generated', handleCardGenerated)
  sock.on('game:number_called', handleNumberCalled)
  sock.on('game:winner_declared', handleWinnerDeclared)

  // Return cleanup function
  return () => {
    sock.off('game:started', handleGameStarted)
    sock.off('game:card_generated', handleCardGenerated)
    sock.off('game:number_called', handleNumberCalled)
    sock.off('game:winner_declared', handleWinnerDeclared)
  }
}

// Re-export Socket type for consumers
export type { Socket }
